"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { withCloudinaryAutoFormat } from "@/lib/cloudinaryImageUrl";

interface GalleryImage {
  _key?: string;
  asset?: {
    secure_url?: string;
    url?: string;
    public_id?: string;
    context?: {
      custom?: {
        alt?: string;
        caption?: string;
      };
    };
  };
  alt?: string;
  caption?: string;
}

interface PressReleaseGalleryProps {
  images?: GalleryImage[];
  title?: string;
}

interface PreparedImage {
  id: string;
  url: string;
  alt: string;
  caption?: string;
}

const PressReleaseGallery = ({ images, title }: PressReleaseGalleryProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const preparedImages: PreparedImage[] = (images || []).flatMap(
    (image, index) => {
      const rawUrl = image?.asset?.secure_url || image?.asset?.url;
      if (!rawUrl) return [];

      return [
        {
          id: image?._key || `gallery-${index}`,
          url: withCloudinaryAutoFormat(rawUrl),
          alt: image?.alt || `Gallery image ${index + 1}`,
          caption: image?.caption,
        },
      ];
    }
  );

  const hasImages = preparedImages.length > 0;
  const isOpen = activeIndex !== null;

  const handleClose = useCallback(() => setActiveIndex(null), []);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) =>
      prev === null ? prev : (prev - 1 + preparedImages.length) % preparedImages.length
    );
  }, [preparedImages.length]);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) =>
      prev === null ? prev : (prev + 1) % preparedImages.length
    );
  }, [preparedImages.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
      if (event.key === "ArrowLeft") handlePrev();
      if (event.key === "ArrowRight") handleNext();
    };

    document.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, handleClose, handlePrev, handleNext]);

  if (!hasImages) return null;

  const activeImage = activeIndex !== null ? preparedImages[activeIndex] : null;

  return (
    <section className="mt-12 border-t border-gray-200 pt-8">
      <h2 className="mb-6 text-2xl font-bold text-gray-900">
        {title || "Gallery"}
      </h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        {preparedImages.map((image, index) => (
          <figure key={image.id} className="m-0">
            <button
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`View ${image.alt} in fullscreen`}
              className="group relative block aspect-square w-full overflow-hidden rounded-xl bg-gray-100 ring-1 ring-gray-200 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <Image
                src={image.url}
                alt={image.alt}
                fill
                sizes="(max-width: 640px) 50vw, 33vw"
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </button>
            {image.caption && (
              <figcaption className="mt-2 line-clamp-2 text-center text-xs text-gray-600">
                {image.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>

      {isOpen && activeImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 sm:p-8"
          onClick={handleClose}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close fullscreen"
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X className="size-6" />
          </button>

          {preparedImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handlePrev();
                }}
                aria-label="Previous image"
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-4"
              >
                <ChevronLeft className="size-7" />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleNext();
                }}
                aria-label="Next image"
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-4"
              >
                <ChevronRight className="size-7" />
              </button>
            </>
          )}

          <figure
            className="relative flex max-h-full w-full max-w-5xl flex-col items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative h-[75vh] w-full">
              <Image
                src={activeImage.url}
                alt={activeImage.alt}
                fill
                sizes="100vw"
                className="object-contain"
                priority
              />
            </div>
            {activeImage.caption && (
              <figcaption className="mt-4 text-center text-sm text-white/80">
                {activeImage.caption}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </section>
  );
};

export default PressReleaseGallery;
